import React from "react";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

export interface SplitParticipant {
  id: string;
  name: string;
  email: string;
}

export interface SplitEditorProps {
  participants: SplitParticipant[];
  /** Total expense amount in dollars */
  totalAmount: number;

  splitType: string;
  onSplitTypeChange: (value: string) => void;

  selectedParticipants: Set<string>;
  onToggleParticipant: (id: string) => void;
  onSelectAll: () => void;

  /** Exact-dollar allocations per userId */
  splitAllocations: Record<string, number>;
  onUpdateAllocation: (id: string, val: number) => void;

  /** Percentage allocations per userId */
  splitPercentages: Record<string, number>;
  onUpdatePercentage: (id: string, val: number) => void;

  /** Share count per userId */
  splitShares: Record<string, number>;
  onUpdateShares: (id: string, val: number) => void;

  /** Adjustment dollar amount per userId */
  adjustmentAmounts: Record<string, number>;
  onUpdateAdjustment: (id: string, val: number) => void;

  validationError?: string | null;
}

const SPLIT_OPTIONS = [
  "Equally",
  "By exact amounts",
  "By percent",
  "By shares",
  "Adjustments",
];

const fmt = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

export function SplitEditor({
  participants,
  totalAmount,
  splitType,
  onSplitTypeChange,
  selectedParticipants,
  onToggleParticipant,
  onSelectAll,
  splitAllocations,
  onUpdateAllocation,
  splitPercentages,
  onUpdatePercentage,
  splitShares,
  onUpdateShares,
  adjustmentAmounts,
  onUpdateAdjustment,
  validationError,
}: SplitEditorProps) {
  return (
    <Box>
      {/* Split type selector */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Split
        </Typography>
        <ToggleButtonGroup
          value={splitType}
          exclusive
          onChange={(_, value) => value && onSplitTypeChange(value)}
          size="small"
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          {SPLIT_OPTIONS.map((option) => (
            <ToggleButton
              key={option}
              value={option}
              sx={{ textTransform: "none" }}
            >
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Participant section header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {splitType === "Equally"
            ? "Select who this is split between"
            : "Split details"}
        </Typography>
        {splitType === "Equally" && (
          <Button
            size="small"
            variant="text"
            onClick={onSelectAll}
            sx={{ fontSize: "0.75rem", p: 0.5 }}
          >
            All
          </Button>
        )}
      </Box>

      {/* Participant rows */}
      <Stack spacing={1.5}>
        {participants.map((participant) => {
          const isSelected = selectedParticipants.has(participant.id);
          const allocation = splitAllocations[participant.id] ?? 0;
          const percentage = splitPercentages[participant.id] ?? 0;
          const shares = splitShares[participant.id] ?? 1;
          const adjustment = adjustmentAmounts[participant.id] ?? 0;

          return (
            <Box
              key={participant.id}
              sx={{
                p: 1.5,
                bgcolor: "white",
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {(splitType === "Equally" || splitType === "Adjustments") && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => onToggleParticipant(participant.id)}
                  style={{ cursor: "pointer" }}
                />
              )}

              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {participant.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {participant.email}
                </Typography>
              </Box>

              {splitType === "By exact amounts" && (
                <TextField
                  type="number"
                  placeholder="0.00"
                  value={allocation}
                  onChange={(e) =>
                    onUpdateAllocation(
                      participant.id,
                      parseFloat(e.target.value) || 0
                    )
                  }
                  size="small"
                  inputProps={{ step: "0.01", min: "0" }}
                  sx={{ width: 100 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                />
              )}

              {splitType === "By percent" && (
                <TextField
                  type="number"
                  placeholder="0"
                  value={percentage}
                  onChange={(e) =>
                    onUpdatePercentage(
                      participant.id,
                      parseFloat(e.target.value) || 0
                    )
                  }
                  size="small"
                  inputProps={{ step: "0.01", min: "0", max: "100" }}
                  sx={{ width: 90 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                />
              )}

              {splitType === "By shares" && (
                <TextField
                  type="number"
                  placeholder="1"
                  value={shares}
                  onChange={(e) =>
                    onUpdateShares(
                      participant.id,
                      parseFloat(e.target.value) || 0
                    )
                  }
                  size="small"
                  slotProps={{ htmlInput: { step: "1", min: "0" } }}
                  sx={{ width: 80 }}
                />
              )}

              {splitType === "Adjustments" && isSelected && (
                <TextField
                  type="number"
                  placeholder="0.00"
                  value={adjustment}
                  onChange={(e) =>
                    onUpdateAdjustment(
                      participant.id,
                      parseFloat(e.target.value) || 0
                    )
                  }
                  size="small"
                  inputProps={{ step: "0.01" }}
                  sx={{ width: 100 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+$</InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>
          );
        })}
      </Stack>

      {/* Preview / summary */}
      {totalAmount > 0 && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1.5 }}>
          {validationError && (
            <Typography
              variant="body2"
              sx={{ color: "error.main", fontWeight: 600, mb: 1 }}
            >
              ⚠️ {validationError}
            </Typography>
          )}

          {splitType === "Equally" && selectedParticipants.size > 0 && (
            <Stack spacing={0.5}>
              {participants
                .filter((p) => selectedParticipants.has(p.id))
                .map((p) => (
                  <Typography key={p.id} variant="body2" color="text.secondary">
                    {p.name}: {fmt(totalAmount / selectedParticipants.size)}
                  </Typography>
                ))}
            </Stack>
          )}

          {splitType === "By exact amounts" && (
            <Stack spacing={0.5}>
              {participants
                .filter((p) => (splitAllocations[p.id] ?? 0) > 0)
                .map((p) => (
                  <Typography key={p.id} variant="body2" color="text.secondary">
                    {p.name}: {fmt(splitAllocations[p.id] || 0)}
                  </Typography>
                ))}
            </Stack>
          )}

          {splitType === "By percent" && (
            <Stack spacing={0.5}>
              {participants
                .filter((p) => (splitPercentages[p.id] ?? 0) > 0)
                .map((p) => {
                  const pct = splitPercentages[p.id] || 0;
                  return (
                    <Typography
                      key={p.id}
                      variant="body2"
                      color="text.secondary"
                    >
                      {p.name}: {pct}% = {fmt((totalAmount * pct) / 100)}
                    </Typography>
                  );
                })}
            </Stack>
          )}

          {splitType === "By shares" &&
            (() => {
              const totalShares = participants.reduce(
                (sum, p) => sum + (splitShares[p.id] ?? 1),
                0
              );
              return (
                <Stack spacing={0.5}>
                  {participants.map((p) => {
                    const s = splitShares[p.id] ?? 1;
                    return (
                      <Typography
                        key={p.id}
                        variant="body2"
                        color="text.secondary"
                      >
                        {p.name}: {s} shares ={" "}
                        {fmt((totalAmount * s) / (totalShares || 1))}
                      </Typography>
                    );
                  })}
                </Stack>
              );
            })()}

          {splitType === "Adjustments" &&
            (() => {
              const selectedIds = participants
                .filter((p) => selectedParticipants.has(p.id))
                .map((p) => p.id);
              const selectedCount = selectedIds.length || 1;
              const baseSplit = totalAmount / selectedCount;
              const adjustedIds = selectedIds.filter(
                (id) => (adjustmentAmounts[id] ?? 0) !== 0
              );
              const totalAdj = adjustedIds.reduce(
                (sum, id) => sum + (adjustmentAmounts[id] ?? 0),
                0
              );
              const remainingCount = selectedCount - adjustedIds.length;
              const offset = remainingCount > 0 ? totalAdj / remainingCount : 0;
              return (
                <Stack spacing={0.5}>
                  {participants
                    .filter((p) => selectedParticipants.has(p.id))
                    .map((p) => {
                      const adj = adjustmentAmounts[p.id] ?? 0;
                      const total = adjustedIds.includes(p.id)
                        ? baseSplit + adj
                        : baseSplit - offset;
                      return (
                        <Typography
                          key={p.id}
                          variant="body2"
                          color="text.secondary"
                        >
                          {p.name}: {fmt(total)}
                          {adj !== 0 ? ` (${adj >= 0 ? "+" : ""}${fmt(adj)} adj)` : ""}
                        </Typography>
                      );
                    })}
                </Stack>
              );
            })()}
        </Box>
      )}
    </Box>
  );
}
